import { errorCode, errorDetail, type ErrorCode } from "../generate/errorCode.js";
import { Tooltip } from "./Tooltip.js";

const GENERATION_HELP: Record<ErrorCode, string> = {
  "GEN-CREDITS": "There is not enough balance to create this image. Ask your workspace admin to add balance, then try again.",
  "GEN-REFUSED": "This image could not be created from the supplied references or instructions. Adjust them and try again.",
  "GEN-HELD": "The result has not been confirmed yet. Check its status before starting again so you do not pay twice. Ask your workspace admin if it remains pending.",
  "GEN-SETUP": "Image creation is not available for this workspace yet. Ask your workspace admin to enable it, then try again.",
  "GEN-LIMIT": "The workspace has reached a usage limit or has another request running. Wait for it to finish, then try again. Ask your workspace admin if it continues.",
  "GEN-INPUT": "One of the images or instructions could not be used. Check the required fields, use a supported image, and try again.",
  "GEN-FRAMING": "Review the image framing and product outline. If they look correct you can continue; otherwise create a replacement image.",
  "GEN-COVERAGE": "The print area needs a visual check. Review the boundary and camera openings. A coverage advisory does not stop saving or publishing a template.",
  "GEN-NORMALIZE": "The image could not be prepared for editing. Try loading it again, or ask your workspace admin to check the image.",
  "GEN-REVIEW": "This image changed while you were reviewing it. Refresh the result, review the current image, and try again.",
  "GEN-UNKNOWN": "The image request could not finish. Check its current status and your connection, then try again. Ask your workspace admin if it continues.",
};

/** Customer-safe copy is selected from a catalogue, never from a vendor error.
 * Raw details remain in telemetry. Even the tooltip must not reveal internal tools.
 */
export function publicError(detail: unknown, advisory = false): { code: string; description: string } {
  const text = typeof detail === "string" ? detail : errorDetail(detail);
  if (/template code already in use|code.*already in use|code_conflict/i.test(text)) {
    return { code: "TPL-CODE-IN-USE", description: "Another template already uses this code. Choose the suggested available code or edit the Code / SKU field, then retry saving. Your image and design stay in this draft." };
  }
  if (/panel render failed/i.test(text)) {
    return { code: "APP-RENDER", description: "This panel could not open. Refresh it, or return to another page and try again. If it continues, share this code with your workspace admin." };
  }
  if (/that image could not be used|unsupported image/i.test(text)) {
    return { code: "FORM-IMAGE", description: "Pick a PNG, JPEG or WebP image up to 32 MB, then try again. Your other fields stay unchanged." };
  }
  if (/duplicate|key exists|value exists/i.test(text)) {
    return { code: "FORM-VALUE-IN-USE", description: "This value is already used. Choose a different key or value in the highlighted field, then save again. Your other entries stay in the form." };
  }
  if (/autokey_not_found|no magenta panel|print area could not be found|image could not be read/i.test(text)) {
    return { code: "GEN-COVERAGE", description: "The printable area could not be prepared from this image. Retry the image preparation or ask your workspace admin for help. Your draft has not been discarded." };
  }
  if (/changed elsewhere|version.*(changed|conflict)|stale.*token|token.*stale/i.test(text)) {
    return { code: "TPL-CHANGED", description: "This template changed in another session. Reload the current version before saving again. Keep a copy of your unsaved changes first." };
  }
  if (/geometry|cutout|no print area|mask.*validation/i.test(text)) {
    return { code: "TPL-MASK", description: "The print boundary or a cutout needs adjustment. Review the highlighted geometry, fix it, then save again." };
  }
  if (/device (cap|limit)/i.test(text)) {
    return { code: "AUTH-DEVICE-LIMIT", description: "Your workspace device limit has been reached. Deactivate an unused device in Settings → Signed-in devices, or ask your workspace admin to do so, then sign in again." };
  }
  if (/no save folder chosen|folder not found|no such folder|no file manager/i.test(text)) {
    return { code: "APP-FOLDER", description: "Choose an accessible save folder before rendering. If it cannot be opened, check that the folder still exists and try the Retry action." };
  }
  if (/enter a comment/i.test(text)) {
    return { code: "FORM-REQUIRED", description: "Enter a comment in the highlighted field, then post it again. Your draft stays open." };
  }
  if (/no mock.?up yet/i.test(text)) {
    return { code: "TPL-PREVIEW", description: "Prepare a mock-up preview before publishing. Open the image editor, check the print area, and build the preview, then retry saving." };
  }
  if (/invalid credentials|incorrect password|sign.?in failed|login failed|\b401\b/i.test(text)) {
    return { code: "AUTH-SIGN-IN", description: "Sign-in could not finish. Check your email and password, then try again. If your session expired, sign in again." };
  }
  if (/permission|forbidden|\b403\b/i.test(text)) {
    return { code: "APP-ACCESS", description: "Your account cannot perform this action. Ask your workspace admin to check your access, then try again." };
  }
  if (/network|offline|fetch|connection/i.test(text)) {
    return { code: "APP-CONNECTION", description: "The request could not reach the workspace. Check your internet connection, reconnect, and try again. Your current draft stays open." };
  }
  const known = text.match(/\bGEN-[A-Z]+\b/)?.[0];
  const candidate = known ?? errorCode(text);
  const generation = candidate in GENERATION_HELP ? candidate as ErrorCode : "GEN-UNKNOWN";
  if (generation !== "GEN-UNKNOWN" || known === "GEN-UNKNOWN" || advisory) {
    return { code: generation, description: GENERATION_HELP[generation] };
  }
  return { code: "APP-REQUEST", description: "This action could not finish. Try again. If it continues, share this error code with your workspace admin so they can check the activity log." };
}

export function ErrorNotice({ detail, reference, advisory = false }: {
  detail: unknown; reference?: string | null; advisory?: boolean;
}) {
  const issue = publicError(detail, advisory);
  // Only display recognised job identifiers, never an arbitrary upstream reference.
  const safeReference = reference && /^(?:[0-9a-f]{8}-|job-[0-9a-f]{4})/i.test(reference) ? reference.slice(0, 8) : null;
  const description = issue.description + (safeReference ? ` Reference: ${safeReference}.` : "");
  return (
    <span className="error-notice">
      <code className="error-notice__code">{issue.code}</code>
      <Tooltip text={description}>
        <button type="button" className="info-hint error-notice__help" aria-label={`Explain ${issue.code}`}>
          <span aria-hidden="true">?</span>
        </button>
      </Tooltip>
    </span>
  );
}

/** A field-specific validation message using the same private explanation pattern. */
export function RequiredFieldError({ id, field }: { id: string; field: string }) {
  return <span id={id} className="field-error" role="alert">
    <code className="error-notice__code">FORM-REQUIRED</code>
    <Tooltip text={`Choose or enter ${field}, then save again. The rest of your draft stays open.`}>
      <button type="button" className="info-hint error-notice__help" aria-label={`Explain missing ${field}`}><span aria-hidden="true">?</span></button>
    </Tooltip>
    <span>Choose or enter {field}.</span>
  </span>;
}
